/**
 * A lista acessória que a tela pede **depois** de abrir — a terceira onda da issue #408.
 *
 * A porta da rota responde por quem entra. Ela não responde pelo que a tela busca em seguida:
 * o combo de setores, a busca de CID, o cartão de exames, o bloco de impressoras. Cada uma
 * dessas chamadas tem exigência própria no servidor, e onde ela pede mais que a porta a pessoa
 * entra e encontra caixa vazia — ou, pior, um alerta vermelho de "erro ao carregar" para uma
 * recusa que não é erro nenhum.
 *
 * A medição da issue achou o mesmo defeito em quatorze telas, escrito de duas formas: um
 * `Promise.all` sem tolerância individual, em que a recusa de um combo derruba os outros três,
 * e um `catch` vazio que engole a recusa e deixa o campo mudo. Consertar as quatorze uma a uma
 * refaz o mesmo trabalho quatorze vezes e não impede a décima quinta — por isso a pergunta
 * mora aqui.
 *
 * O que o gancho garante, e que nenhuma tela precisa repetir:
 *
 * 1. **Pergunta antes de pedir.** Recusado, a chamada não sai. Não é otimização: requisição
 *    que já se sabe que volta 403 vira ruído no log do servidor e alerta na tela.
 * 2. **Recusa não é falha.** `bloqueio` e `erro` são campos diferentes de propósito — "seu
 *    perfil não inclui" e "não deu para carregar" pedem reações opostas de quem lê.
 * 3. **O rótulo vem na frente do motivo**, como em `useControle`: em campo e em ícone o aviso
 *    é o nome acessível, e trocando um pelo outro a pessoa ouve o motivo sem saber de quê.
 *
 * ```tsx
 * const setores = useListaPermitida('department.read', 'Setores', carregarSetores);
 * <TextField helperText={setores.bloqueio} disabled={!setores.permitido} />
 * ```
 *
 * A permissão declarada aqui é a mesma que o teste `chamada-cabe-na-porta` cobra do arquivo
 * que faz a chamada. Tirar o gancho para "simplificar" reabre o buraco, e o teste acusa.
 */

import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { avaliarAcao } from './pode';
import { useManifesto } from '../stores/manifestoStore';

export interface ListaGuardada<T> {
  readonly itens: readonly T[];
  readonly carregando: boolean;
  /**
   * Já há resposta para esta lista — vinda do servidor, da falha ou da recusa.
   *
   * Antes disso `itens` está vazia por não se saber ainda, e não por não haver. Quem decide
   * o que desenhar pelo tamanho da lista desenha duas vezes: a versão de lista vazia e, um
   * instante depois, a de verdade.
   */
  readonly resolvida: boolean;
  /** Falso quando o veredito recusou — e aí a chamada nem chegou a sair. */
  readonly permitido: boolean;
  /**
   * Por que a lista está vazia sem que falte cadastro. Ausente quando permitido, e nunca
   * preenchido junto com `erro`: um é recusa de acesso, o outro é falha de carregamento.
   */
  readonly bloqueio?: string;
  readonly erro?: string;
  readonly recarregar: () => void;
}

/**
 * Módulo comercial não é opção aqui: pela régua do V5, contrato encerrado mantém a consulta e
 * fecha só a escrita, e tudo que passa por este gancho é leitura. Aceitar o módulo daria a
 * impressão de que ele muda a resposta.
 */
export interface OpcoesDaLista {
  /** Some com a chamada sem alegar recusa — dependência ainda não escolhida, campo fechado. */
  readonly habilitado?: boolean;
}

/**
 * A frase da recusa de leitura. `avaliarAcao` fala de ação ("Seu perfil não inclui esta
 * ação"), e o que está bloqueado aqui não é um clique: é o conteúdo que a pessoa está olhando.
 */
function frase(rotulo: string, explicacao: string | undefined, motivo: string | undefined): string {
  const porque = motivo === 'permissao' ? 'Seu perfil não inclui este conteúdo.' : explicacao;
  return `${rotulo} — ${porque ?? 'sem acesso'}`;
}

/**
 * O miolo das duas formas: a pergunta de acesso, a chamada que só sai se a resposta for sim,
 * e a separação entre recusa e falha. `carga` é o que voltou — uma lista ou um registro só.
 */
function useCargaPermitida<T>(
  permissao: string,
  rotulo: string,
  carregar: () => Promise<T>,
  vazio: T,
  opcoes: OpcoesDaLista
): { carga: T } & Omit<ListaGuardada<never>, 'itens'> {
  const { manifesto } = useManifesto();
  const { pathname } = useLocation();
  const { habilitado = true } = opcoes;

  const [carga, setCarga] = useState<T>(vazio);
  const [carregando, setCarregando] = useState(false);
  /**
   * A que pergunta a resposta guardada corresponde. Um sinalizador solto ficaria ligado no
   * render em que a pergunta muda e o efeito ainda não rodou — e é justamente aí que a lista
   * vazia passaria por resposta, desenhando a tela duas vezes.
   */
  const [respondidaPara, setRespondidaPara] = useState<string | undefined>(undefined);
  const [erro, setErro] = useState<string | undefined>(undefined);
  const [tentativa, setTentativa] = useState(0);

  const veredito = manifesto
    ? avaliarAcao(pathname, manifesto, { permissao })
    : { liberado: false, motivo: 'carregando' as const, explicacao: 'Carregando seu acesso...' };

  const permitido = veredito.liberado;
  const bloqueio = permitido ? undefined : frase(rotulo, veredito.explicacao, veredito.motivo);

  // Tudo o que faz a resposta mudar. A pergunta muda no mesmo render em que a condição muda,
  // então não há instante em que a resposta anterior passe pela nova.
  const pergunta = `${permitido}|${habilitado}|${tentativa}`;
  // Sem manifesto o veredito ainda é "carregando" e recusa nenhuma é definitiva — a recusa
  // guardada é a de "ainda não sei", e devolvê-la como resposta traz de volta o desenho duplo.
  const resolvida = Boolean(manifesto) && respondidaPara === pergunta;

  useEffect(() => {
    if (!permitido || !habilitado) {
      setCarga(vazio);
      setErro(undefined);
      setRespondidaPara(pergunta);
      return;
    }

    let vivo = true;
    setCarregando(true);
    setErro(undefined);

    carregar()
      .then((resposta) => {
        if (vivo) setCarga(resposta);
      })
      .catch((falha: unknown) => {
        if (!vivo) return;
        setCarga(vazio);
        setErro(falha instanceof Error ? falha.message : `Não foi possível carregar ${rotulo}.`);
      })
      .finally(() => {
        if (!vivo) return;
        setCarregando(false);
        setRespondidaPara(pergunta);
      });

    return () => {
      vivo = false;
    };
    // `vazio` é a constante do chamador e não entra: uma lista literal mudaria de identidade a
    // cada render e refaria a chamada para sempre.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permitido, habilitado, carregar, rotulo, tentativa, pergunta]);

  const recarregar = useCallback(() => setTentativa((anterior) => anterior + 1), []);

  return { carga, carregando, resolvida, permitido, bloqueio, erro, recarregar };
}

const NENHUM: readonly never[] = [];

export function useListaPermitida<T>(
  permissao: string,
  rotulo: string,
  carregar: () => Promise<readonly T[]>,
  opcoes: OpcoesDaLista = {}
): ListaGuardada<T> {
  const { carga, ...resto } = useCargaPermitida<readonly T[]>(
    permissao,
    rotulo,
    carregar,
    NENHUM,
    opcoes
  );
  return { itens: carga, ...resto };
}

export interface DadoGuardado<T> extends Omit<ListaGuardada<never>, 'itens'> {
  /** Nulo enquanto carrega, quando a recusa impediu a chamada e quando ela falhou. */
  readonly dado: T | null;
}

/**
 * A mesma pergunta para o registro que não é lista — o cabeçalho do prestador na tela de
 * exames dele, por exemplo. Antes daqui a recusa caía no `catch` e a tela mandava a pessoa
 * para outra que ela também não abre, o que se lê como defeito do sistema.
 */
export function useDadoPermitido<T>(
  permissao: string,
  rotulo: string,
  carregar: () => Promise<T>,
  opcoes: OpcoesDaLista = {}
): DadoGuardado<T> {
  const { carga, ...resto } = useCargaPermitida<T | null>(
    permissao,
    rotulo,
    carregar,
    null,
    opcoes
  );
  return { dado: carga, ...resto };
}
